import * as React from "react"
import {Divider, Form, Grid, Header, Loader, Message, Segment} from "semantic-ui-react"
import {StateContainer} from "./StateContainer"
import {
  AddBookingState,
  BookerProp,
  DefaultLocaleProp,
  emptyBookingDetails,
} from "./AddBookingWizard"
import {GraphQL} from "../graphql"
import {errorUnionToEither, handleError} from "../graphql/errors"
import {
  Booker,
  BookerFragFragment,
  DefaultLocale,
  EnsureJourney,
  LocaleFragment,
  StripeCardFragment,
} from "../graphql/queries"
import {TimeField} from "./ScheduleFields"
import {BookingDetailsState, bookingDetailsStateToBookingDetails, MapState} from "./BookingDetails"
import {PlaceFields} from "./PlaceFields"
import {history, WithModalStore} from "../App"
import {PeopleMoverField} from "./VehicleWidget"
import {ExtrasFields} from "./ExtrasFields"
import {Charges} from "./Charges"
import {persistedState} from "../localStorage"
import {some} from "fp-ts/lib/Option"
import {journeyToInputJourney} from "../model/journey"
import {UrlLocaleIdProp} from "../urlLocale"

interface Props extends WithModalStore {}

interface AllProps extends Props, DefaultLocaleProp, BookerProp, UrlLocaleIdProp {}

function PricingPage(props: AllProps) {
  const {booker, modalStore} = props

  const emptyDetails: PricingState = {
    ...emptyBookingDetails(props),
    willRegisterCreditCard: false,
  }

  return <>
    <Header as="h1">Price Estimate</Header>
    <Grid stackable columns={2}>
      <Grid.Column>
        <Segment.Group>
          <Segment inverted color="olive">
            <StateContainer
              initialState={emptyDetails}
              render={({state, mapState}) => {
                return (
                  <PricingForm
                    booker={booker}
                    state={state}
                    mapState={mapState}
                    modalStore={modalStore}
                  />
                )
              }}
            />
          </Segment>
        </Segment.Group>
      </Grid.Column>
    </Grid>
  </>
}

interface PricingState extends BookingDetailsState {
  willRegisterCreditCard: boolean
}

interface PricingFormProps extends WithModalStore, BookerProp {
  state: PricingState
  mapState: MapState<PricingState>
}

const dummyStripeCard: StripeCardFragment = {
  id: "1234",
  last4: null,
  brand: null,
  expiryYear: null,
  expiryMonth: null,
  bookerName: "Frodo",
  bookerId: "1234",
}

function addBookingState(booker: BookerFragFragment | null, state: PricingState): AddBookingState {
  return {
    forBooker: booker,
    bookerFormOpen: false,
    details: state,
    stripeCard: {
      card: null,
      loading: false,
    },
    paymentLink: {
      toEmail: null,
      editing: false,
    },
    promotion: {
      promoCode: null,
      giftLoyaltyCredits: false,
    },
    notes: "",
    paymentSectionOpen: false,
  }
}

function isLocalePriced(locale: LocaleFragment) {
  return ["VIC", "NSW"].includes(locale.label)
}

function PricingForm(props: PricingFormProps) {
  const {state, mapState, modalStore, booker} = props

  const okJourney = state.journey.origin && state.journey.destination
  const canPrice = isLocalePriced(state.locale)

  return (
    <Form>
      <TimeField
        disabled={false}
        error={null}
        state={state}
        mapState={(fn) => {
          mapState((state) => {
            return {...state, ...fn(state)}
          })
        }}
      />
      <Message>
        <p>
          {canPrice
            ? 'For indicative pricing just enter a suburb such as "Bentleigh".'
            : "Pricing is not available for that region, please call us on 1300 C U Soon (1300 287 666)"}
        </p>
      </Message>
      <PlaceFields
        modalStore={modalStore}
        wantFlightNumber={false}
        disabled={false}
        state={state}
        mapState={(fn) => {
          mapState((state) => {
            return {...state, ...fn(state)}
          })
        }}
        onOptimizeWaypoints={null}
      />
      {okJourney && (
        <>
          <PeopleMoverField
            state={state}
            mapState={(fn) => {
              mapState((state) => {
                return {...state, ...fn(state)}
              })
            }}
            disabled={false}
          />
          <CreditCardField
            state={state}
            mapState={(fn) => {
              mapState((state) => {
                return {...state, ...fn(state)}
              })
            }}
          />
          <ExtrasFields
            state={state}
            mapState={(fn) => {
              mapState((state) => {
                return {...state, ...fn(state)}
              })
            }}
            disabled={false}
          />
          <Divider />
          <GraphQL.Query
            query={EnsureJourney}
            variables={{journey: journeyToInputJourney(state.journey)}}
            render={({data}) => {
              if (data) {
                return (
                  <Charges
                    forBooker={booker}
                    journeyId={data.ensureJourney.id}
                    booking={bookingDetailsStateToBookingDetails(state)}
                    initialLoyaltyProgramDiscount={false}
                    giftLoyaltyProgramDiscount={false}
                    stripeCard={state.willRegisterCreditCard ? dummyStripeCard : null}
                    state={{
                      price: null,
                      editingPrice: false,
                    }}
                    mapState={null}
                    isAdmin={booker ? booker.isAdmin : false}
                    extraCharges={[]}
                    onApprovePrice={null}
                    modalStore={modalStore}
                  />
                )
              }
              return <Loader inline active />
            }}
          />
          <Message>
            <p>The price is estimated based on the information you provide.</p>
          </Message>
          <Divider />
          <Form.Group>
            <Form.Button
              positive
              onClick={(e) => {
                e.preventDefault()
                persistedState.value = some(addBookingState(booker, state))
                history.push("/bookings/add")
              }}
              content="Make a booking"
            />
          </Form.Group>
        </>
      )}
    </Form>
  )
}

interface CreditCardState {
  willRegisterCreditCard: boolean
}

function CreditCardField(props: {state: CreditCardState; mapState: MapState<CreditCardState>}) {
  const {
    mapState,
    state: {willRegisterCreditCard},
  } = props
  return (
    <Form.Field>
      <Form.Checkbox
        label="Will you supply a credit card?"
        checked={willRegisterCreditCard}
        onChange={(e, checkbox) => {
          mapState((state) => ({...state, willRegisterCreditCard: checkbox.checked || false}))
        }}
      />
    </Form.Field>
  )
}

function WithBooker(props: Props & DefaultLocaleProp & UrlLocaleIdProp) {
  return (
    <GraphQL.Query
      query={Booker}
      variables={{}}
      render={({data}) => {
        if (data !== null) {
          return handleError(errorUnionToEither(data.booker), (data) => (
            <PricingPage {...props} booker={data.booker} />
          ))
        }
        // This should not happen, as Booker is loaded in InitializeSession
        return <Loader active />
      }}
    />
  )
}

function WithDefaultLocale(props: Props & UrlLocaleIdProp) {
  return (
    <GraphQL.Query
      query={DefaultLocale}
      variables={{
        id: props.urlLocaleId
      }}
      render={({data}) => {
        if (data) {
          return <WithBooker {...props} defaultLocale={data.defaultLocale} />
        }
        return <Loader active />
      }}
    />
  )
}

export {WithDefaultLocale as PricingPage}



// WEBPACK FOOTER //
// ./src/components/PricingPage.tsx